import { useRef, useCallback } from 'react';

let cachedWasmBinary = null;

export const useNoiseSuppression = () => {
    const audioContextRef = useRef(null);
    const sourceNodeRef = useRef(null);
    const suppressorNodeRef = useRef(null);
    const bypassGainRef = useRef(null);
    const suppressorGainRef = useRef(null);
    const destinationNodeRef = useRef(null);
    const rawStreamRef = useRef(null);

    const isSupported = useCallback(() => {
        return typeof AudioContext !== 'undefined' &&
            typeof AudioWorkletNode !== 'undefined';
    }, []);

    const cleanup = useCallback(() => {
        [sourceNodeRef, suppressorNodeRef, bypassGainRef, suppressorGainRef, destinationNodeRef].forEach(ref => {
            if (ref.current) {
                try { ref.current.disconnect(); } catch {}
                ref.current = null;
            }
        });
        if (rawStreamRef.current) {
            rawStreamRef.current.getTracks().forEach(t => t.stop());
            rawStreamRef.current = null;
        }
        if (audioContextRef.current) {
            audioContextRef.current.close().catch(() => {});
            audioContextRef.current = null;
        }
    }, []);

    /**
     * Toggle by adjusting gain values — no disconnect/reconnect, no pops.
     * RNNoise keeps its recurrent neural network state warm across toggles.
     */
    const setNoiseCancellation = useCallback((enabled) => {
        const bypassGain = bypassGainRef.current;
        const suppressorGain = suppressorGainRef.current;
        if (!bypassGain || !suppressorGain) return;

        if (enabled) {
            bypassGain.gain.value = 0;
            suppressorGain.gain.value = 1;
        } else {
            bypassGain.gain.value = 1;
            suppressorGain.gain.value = 0;
        }
    }, []);

    /**
     * Called once per call. Builds the full pipeline upfront so toggles are instant.
     * Two parallel paths gated by gain nodes:
     *   suppressor path: source → suppressor → suppressorGain → destination
     *   bypass path:     source → bypassGain  → destination
     *
     * @param {string} micDeviceId
     * @param {boolean} enableNoiseSuppression
     * @returns {Promise<MediaStream>}
     */
    const getProcessedStream = useCallback(async (micDeviceId, enableNoiseSuppression) => {
        cleanup();

        const baseConstraints = {
            noiseSuppression: false,    // RNNoise handles suppression
            echoCancellation: true,
            autoGainControl: false,     // AGC pumping + RNNoise = intermittent noise artifacts
            googNoiseSuppression: false,
            googHighpassFilter: true,
            googNoiseSuppression2: false,
        };
        const constraints = {
            audio: micDeviceId
                ? { deviceId: { exact: micDeviceId }, ...baseConstraints }
                : baseConstraints,
            video: false,
        };

        const rawStream = await navigator.mediaDevices.getUserMedia(constraints);
        rawStreamRef.current = rawStream;

        if (!isSupported()) {
            console.warn('AudioWorklet not supported, using raw stream');
            return rawStream;
        }

        try {
            const audioContext = new AudioContext({ sampleRate: 48000 });
            audioContextRef.current = audioContext;

            if (audioContext.state === 'suspended') {
                await audioContext.resume();
            }

            const processorUrl = new URL(
                '@sapphi-red/web-noise-suppressor/rnnoiseWorklet.js',
                import.meta.url
            );
            await audioContext.audioWorklet.addModule(processorUrl);

            if (!cachedWasmBinary) {
                const wasmUrl = new URL(
                    '@sapphi-red/web-noise-suppressor/rnnoise.wasm',
                    import.meta.url
                );
                const resp = await fetch(wasmUrl);
                cachedWasmBinary = await resp.arrayBuffer();
            }

            const { RnnoiseWorkletNode } = await import('@sapphi-red/web-noise-suppressor');
            const suppressorNode = new RnnoiseWorkletNode(audioContext, {
                wasmBinary: cachedWasmBinary.slice(0),
                maxChannels: 1,
            });
            suppressorNodeRef.current = suppressorNode;

            const sourceNode = audioContext.createMediaStreamSource(rawStream);
            sourceNodeRef.current = sourceNode;

            const destinationNode = audioContext.createMediaStreamDestination();
            destinationNodeRef.current = destinationNode;

            const bypassGain = audioContext.createGain();
            const suppressorGain = audioContext.createGain();
            bypassGainRef.current = bypassGain;
            suppressorGainRef.current = suppressorGain;

            // Suppressor path (always connected, gated by gain)
            sourceNode.connect(suppressorNode);
            suppressorNode.connect(suppressorGain);
            suppressorGain.connect(destinationNode);

            // Bypass path (always connected, gated by gain)
            sourceNode.connect(bypassGain);
            bypassGain.connect(destinationNode);

            if (enableNoiseSuppression) {
                suppressorGain.gain.value = 1;
                bypassGain.gain.value = 0;
            } else {
                suppressorGain.gain.value = 0;
                bypassGain.gain.value = 1;
            }

            console.log('Audio pipeline ready, NC:', enableNoiseSuppression);
            return destinationNode.stream;
        } catch (error) {
            console.error('Audio pipeline failed, falling back to raw stream:', error);
            [sourceNodeRef, suppressorNodeRef, bypassGainRef, suppressorGainRef, destinationNodeRef].forEach(ref => {
                if (ref.current) { try { ref.current.disconnect(); } catch {} ref.current = null; }
            });
            if (audioContextRef.current) { audioContextRef.current.close().catch(() => {}); audioContextRef.current = null; }
            return rawStream;
        }
    }, [cleanup, isSupported]);

    return {
        getProcessedStream,
        setNoiseCancellation,
        cleanup,
        isSupported: isSupported(),
    };
};
